import { Link } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowUpRight, FileText } from "lucide-react";
import { CATEGORIES } from "@/data/products";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang, EN_CATEGORY_LABELS } from "@/i18n";
import { EN_TAGS } from "@/data/productsEn";

export default function ProductsPage() {
    const { t } = useLang();
    useSeo(
        t(
            "Produkte: Kopfstationen, Umsetzer & Verteiltechnik | Müller",
            "Products: headends, transmodulators & distribution | Müller"
        ),
        t(
            "Das komplette Programm: eigene Müller-Kopfstationen, Einzelumsetzer, Antennenheizungen, Jultec- und Kathrein-Technik — ab Lager lieferbar durch Müller Hemhofen.",
            "The complete programme: our own Müller headends, transmodulators, antenna heating, Jultec and Kathrein technology — ex stock from Müller Hemhofen."
        ),
    );
    return (
        <main data-testid="page-products" className="pt-28 sm:pt-32">
            <section className="py-10 sm:py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <Eyebrow>{t("Produktwelt", "Products")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            {t("Alle Produktkategorien im Überblick.", "All product categories at a glance.")}
                        </h1>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
                            {t(
                                "Von der Kopfstation bis zur Antennenheizung: Unsere neun Produktwelten decken die komplette Empfangs- und Verteiltechnik ab. Alle Katalogartikel sind Lagerware — Lieferung DE & EU.",
                                "From headends to antenna heating: our nine product lines cover the complete reception and distribution technology. All catalogue items are held in stock — delivery in DE & the EU."
                            )}
                        </p>
                        <a
                            href="/datenblaetter/katalog_ohne_preise.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="products-katalog-link"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-6 py-3 transition-colors"
                        >
                            <FileText className="h-4 w-4" />
                            {t("Fachhandelskatalog (PDF)", "Trade catalogue (PDF)")}
                        </a>
                    </Reveal>
                </div>
            </section>
            <section className="pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {CATEGORIES.map((c, i) => (
                            <Reveal key={c.slug} delay={(i % 3) * 0.07}>
                                <Link
                                    to={`/produkte/${c.slug}`}
                                    data-testid={`product-card-${c.slug}`}
                                    className="group flex h-full flex-col rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <span className="eyebrow text-[9.5px] px-2 py-1 rounded-sm border text-slate-600 bg-slate-100 border-slate-200">
                                            {t(c.tag, EN_TAGS[c.tag] || c.tag)}
                                        </span>
                                        <c.icon className="h-5 w-5 shrink-0 text-red-700" />
                                    </div>
                                    <h2 className="mt-6 font-display text-xl font-bold text-slate-900">{t(c.label, EN_CATEGORY_LABELS[c.slug] || c.label)}</h2>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600 flex-1">{c.short}</p>
                                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-700">
                                        {t("Zur Kategorie", "To the category")}
                                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
